import { Alert, CircularProgress } from "@mui/material";
import * as React from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";

import {
  ExpandableCopyText,
  PageBreadcrumbs,
  PageLayout,
} from "@/shared/components";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useSession } from "@/shared/domain/sessions/hooks";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import { SessionChrome } from "./SessionChrome";
import styles from "./SessionDetail.module.css";
import { SessionLayout } from "./SessionLayout";
import { isBareSessionUrl, SessionTab } from "./useActiveSessionTabFromUrl";

/**
 * Detail page for a Session at ``/sessions/:sessionId``.
 *
 * ``SessionDetailShell`` renders the title and breadcrumb across all
 * three render branches:
 *
 * 1. **Loading** — shows the breadcrumb and a spinner body.
 * 2. **Success** — the resolved ``SessionRecord`` flows to the body
 *    (``SessionLayout``) from a single ``useSession`` call, so a fetch
 *    failure raises exactly one ``ErrorBoundary``.
 * 3. **Error** — the body swaps to ``SessionErrorFallback``; the
 *    breadcrumb, tab strip, and back-link stay reachable.
 *
 * 4XX bodies surface verbatim (the backend shapes them for users);
 * everything else falls back to generic copy via
 * :func:`userFacingErrorMessage`.
 */
export function SessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();

  if (sessionId === undefined) {
    // Programming error — the route declaration registers
    // `/sessions/:sessionId` so React Router guarantees the param is
    // set by the time this component mounts.
    throw new Error("sessionId not provided");
  }

  return (
    <ErrorBoundary
      fallback={({ error }) => (
        <SessionDetailShell sessionId={sessionId}>
          <SessionErrorFallback error={error} sessionId={sessionId} />
        </SessionDetailShell>
      )}
    >
      <React.Suspense
        fallback={
          <SessionDetailShell sessionId={sessionId}>
            <div className={styles.loadingContainer}>
              <CircularProgress size="1.5rem" />
            </div>
          </SessionDetailShell>
        }
      >
        <SessionDetailLoaded sessionId={sessionId} />
      </React.Suspense>
    </ErrorBoundary>
  );
}

/**
 * Sole ``useSession`` call site. The resolved record flows by prop to
 * ``SessionLayout`` so the page has one cache subscription and one
 * suspense throw, keeping loading and error transitions consolidated
 * in the body while the shell stays stable.
 */
function SessionDetailLoaded({ sessionId }: { sessionId: string }) {
  const { data: session } = useSession({ sessionId });

  return (
    <SessionDetailShell sessionId={sessionId}>
      <SessionLayout session={session} />
    </SessionDetailShell>
  );
}

/**
 * Page chrome shared by every render branch. Renders ``PageLayout``
 * with the title-bar breadcrumb and accepts the body as children, so
 * the breadcrumb and document title stay stable across loading,
 * success, and error states.
 */
function SessionDetailShell({
  sessionId,
  children,
}: {
  sessionId: string;
  children: React.ReactNode;
}) {
  return (
    <PageLayout
      title={`Roboto - Session - ${sessionId}`}
      headerContentLeft={
        <PageBreadcrumbs>
          <Link to={Goto.route.Sessions}>Sessions</Link>
          <ExpandableCopyText text={sessionId} trimLength={36} />
        </PageBreadcrumbs>
      }
    >
      {children}
    </PageLayout>
  );
}

interface SessionErrorFallbackProps {
  sessionId: string;
  error: Error;
}

/**
 * Error-state body. Renders the shared ``SessionChrome`` with no
 * resolved record (heading falls back to the URL session id; metadata
 * row is omitted) and surfaces the error message in an alert.
 *
 * When the user lands on the bare ``/sessions/:sessionId`` URL, the
 * index route's ``<Navigate>`` only renders inside ``SessionLayout``'s
 * ``<Outlet>`` — and on error, ``SessionLayout`` is unmounted. This
 * fallback dispatches the same redirect so the URL and the tab strip
 * stay in sync.
 */
function SessionErrorFallback({ sessionId, error }: SessionErrorFallbackProps) {
  const location = useLocation();
  const isBareUrl = isBareSessionUrl(location.pathname, sessionId);

  return (
    <div className={styles.root}>
      {isBareUrl && <Navigate to={SessionTab.Topics} replace />}
      <SessionChrome sessionId={sessionId} session={null} />
      <div className={styles.tabPanel}>
        <Alert severity="error">
          {userFacingErrorMessage(
            error,
            <>
              Failed to load session. Please contact{" "}
              <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
              problem persists.
            </>,
          )}
        </Alert>
      </div>
    </div>
  );
}
